import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminModalComponent,
  AdminTableComponent,
  AdminTextareaComponent,
} from '../../admin';
import { AdminConfirmDialogService } from '../../admin/admin-confirm-dialog.service';
import { AdminToastService } from '../../admin/admin-toast.service';
import {
  CrmTasksService,
  CRM_TASK_DEFAULT_REMINDER_OFFSET_MINUTES,
  CRM_TASK_REMINDER_OPTIONS,
  type CreateCrmTaskPayload,
  type CrmDetailArea,
  type CrmTaskDto,
  type CrmTaskEntityType,
  type CrmTaskReminderStatus,
  type CrmTaskStatus,
  type UpdateCrmTaskPayload,
} from '../../services/crm-tasks.service';
import { TaskReminderRefreshService } from '../../services/task-reminder-refresh.service';

export type CrmTaskAssigneeOption = {
  userId: string;
  label: string;
};

const TASK_STATUS_OPTIONS: Array<{ value: CrmTaskStatus; label: string }> = [
  { value: 'NEW', label: 'New' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

const TASK_REMINDER_OPTIONS = CRM_TASK_REMINDER_OPTIONS;

type TaskFormState = {
  title: string;
  description: string;
  assignedTo: string;
  dueAtLocal: string;
  reminderOffsetMinutes: number;
};

const emptyTaskForm = (): TaskFormState => ({
  title: '',
  description: '',
  assignedTo: '',
  dueAtLocal: '',
  reminderOffsetMinutes: CRM_TASK_DEFAULT_REMINDER_OFFSET_MINUTES,
});

@Component({
  selector: 'app-crm-tasks-section',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    AdminCardComponent,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminIconComponent,
    AdminModalComponent,
    AdminTableComponent,
    AdminTextareaComponent,
  ],
  template: `
    <app-admin-card>
      <div class="tasks-section__head">
        <h3 class="tasks-section__title">
          <app-admin-icon name="clipboard-check" [size]="18" />
          Tasks
          @if (visibleTasks().length > 0) {
            <span class="tasks-section__count">({{ visibleTasks().length }})</span>
          }
        </h3>
        <div class="tasks-section__head-actions">
          @if (tasks().length > 0) {
            <label class="tasks-section__filter">
              <input
                type="checkbox"
                class="tasks-section__filter-input"
                [ngModel]="showCompletedTasks()"
                (ngModelChange)="showCompletedTasks.set($event)"
              />
              <span>Show completed tasks</span>
            </label>
          }
          @if (!isArchived()) {
            <app-admin-button variant="primary" type="button" (click)="openCreateModal()">
              <app-admin-icon name="plus" [size]="16" />
              Add Task
            </app-admin-button>
          }
        </div>
      </div>

      @if (loading()) {
        <p class="cg-form-help">Loading tasks…</p>
      } @else if (tasks().length === 0) {
        <app-admin-empty-state
          icon="clipboard-check"
          title="No tasks yet"
          description="Create tasks to track follow-ups and assignments for this record."
        />
      } @else if (visibleTasks().length === 0) {
        <app-admin-empty-state
          icon="clipboard-check"
          [title]="emptyFilterTitle()"
          [description]="emptyFilterDescription()"
        />
      } @else {
        <app-admin-table>
          <thead>
            <tr>
              <th>Title</th>
              <th>Assigned user</th>
              <th>Due date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            @for (task of visibleTasks(); track task.id) {
              <tr>
                <td class="tasks-section__title-cell">
                  <button
                    type="button"
                    class="tasks-section__title-link"
                    (click)="openDetailModal(task)"
                  >
                    {{ task.title }}
                  </button>
                </td>
                <td>{{ task.assignedToDisplayName || task.assignedTo }}</td>
                <td>{{ task.dueAt ? (task.dueAt | date: 'medium') : '—' }}</td>
                <td>
                  <span class="tasks-section__status" [class]="statusClass(task.status)">
                    {{ task.statusLabel }}
                  </span>
                </td>
                <td>
                  <div class="tasks-section__actions">
                    @if (!isArchived()) {
                      <label class="tasks-section__status-select-wrap">
                        <span class="sr-only">Update status for {{ task.title }}</span>
                        <select
                          class="cg-select tasks-section__status-select"
                          [ngModel]="task.status"
                          (ngModelChange)="onStatusChange(task, $event)"
                          [disabled]="actionLoadingId() === task.id"
                        >
                          @for (option of statusOptions; track option.value) {
                            <option [value]="option.value">{{ option.label }}</option>
                          }
                        </select>
                      </label>
                      <button
                        type="button"
                        class="tasks-section__icon-btn"
                        aria-label="Edit task"
                        [disabled]="actionLoadingId() === task.id"
                        (click)="openEditModal(task)"
                      >
                        <app-admin-icon name="pencil" [size]="15" />
                      </button>
                      <button
                        type="button"
                        class="tasks-section__icon-btn tasks-section__icon-btn--danger"
                        aria-label="Delete task"
                        [disabled]="actionLoadingId() === task.id"
                        (click)="confirmDelete(task)"
                      >
                        <app-admin-icon name="trash-2" [size]="15" />
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </app-admin-table>
      }
    </app-admin-card>

    @if (formModalOpen()) {
      <app-admin-modal
        [title]="editingTask() ? 'Edit task' : 'Add task'"
        size="lg"
        (closed)="closeFormModal()"
      >
        <div class="tasks-section__form">
          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Task title</span>
            <input
              class="cg-input"
              type="text"
              name="taskTitle"
              [(ngModel)]="form.title"
              maxlength="200"
              [disabled]="saving()"
            />
          </label>

          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Task description</span>
            <app-admin-textarea
              name="taskDescription"
              [(value)]="form.description"
              [rows]="3"
              [fullWidth]="true"
              [disabled]="saving()"
              placeholder="Optional details about this task"
            />
          </label>

          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Assigned to</span>
            <select
              class="cg-select"
              name="taskAssignedTo"
              [(ngModel)]="form.assignedTo"
              [disabled]="saving() || assignableUsers().length === 0"
            >
              <option value="">Select a user…</option>
              @for (user of assignableUsers(); track user.userId) {
                <option [value]="user.userId">{{ user.label }}</option>
              }
            </select>
          </label>

          <label class="cg-admin-field">
            <span class="cg-admin-field__label">Due date</span>
            <input
              class="cg-input"
              type="datetime-local"
              name="taskDueAt"
              [(ngModel)]="form.dueAtLocal"
              [min]="minDueAtLocal()"
              (ngModelChange)="onDueAtChange($event)"
              [disabled]="saving()"
            />
          </label>

          @if (form.dueAtLocal) {
            <label class="cg-admin-field">
              <span class="cg-admin-field__label">Reminder</span>
              <select
                class="cg-select"
                name="taskReminderOffset"
                [(ngModel)]="form.reminderOffsetMinutes"
                [disabled]="saving()"
              >
                @for (option of reminderOptions; track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
            </label>

            <div class="tasks-section__notify-readonly">
              <span class="tasks-section__notify-label">Following users will be notified</span>
              <ul class="tasks-section__notify-list">
                @for (user of buildNotifiedUsers(); track user.userId) {
                  <li>
                    <span class="tasks-section__notify-name">{{ user.label }}</span>
                    <span class="tasks-section__notify-role">{{ user.role }}</span>
                  </li>
                }
              </ul>
            </div>
          }

          @if (formError()) {
            <p class="tasks-section__error" role="alert">{{ formError() }}</p>
          }

          <div class="tasks-section__form-actions">
            <app-admin-button
              variant="primary"
              type="button"
              [disabled]="saving() || !canSubmitForm()"
              (click)="submitForm()"
            >
              {{ saving() ? 'Saving…' : editingTask() ? 'Save changes' : 'Create task' }}
            </app-admin-button>
            <app-admin-button variant="secondary" type="button" [disabled]="saving()" (click)="closeFormModal()">
              Cancel
            </app-admin-button>
          </div>
        </div>
      </app-admin-modal>
    }

    @if (detailModalOpen() && selectedTask(); as task) {
      <app-admin-modal [title]="task.title" size="lg" (closed)="closeDetailModal()">
        <div class="tasks-section__detail">
          <div class="tasks-section__detail-grid">
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Title</span>
              <p class="tasks-section__detail-value">{{ task.title }}</p>
            </div>
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Status</span>
              @if (isArchived()) {
                <p class="tasks-section__detail-value">
                  <span class="tasks-section__status" [class]="statusClass(task.status)">
                    {{ task.statusLabel }}
                  </span>
                </p>
              } @else {
                <select
                  class="cg-select tasks-section__detail-status-select"
                  [ngModel]="task.status"
                  (ngModelChange)="onStatusChange(task, $event)"
                  [disabled]="actionLoadingId() === task.id"
                >
                  @for (option of statusOptions; track option.value) {
                    <option [value]="option.value">{{ option.label }}</option>
                  }
                </select>
              }
            </div>
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Assigned to</span>
              <p class="tasks-section__detail-value">
                {{ task.assignedToDisplayName || task.assignedTo }}
              </p>
            </div>
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Created by</span>
              <p class="tasks-section__detail-value">
                {{ task.createdByDisplayName || task.createdBy }}
              </p>
            </div>
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Created date</span>
              <p class="tasks-section__detail-value">
                {{ task.createdAt ? (task.createdAt | date: 'medium') : '—' }}
              </p>
            </div>
            <div class="tasks-section__detail-field">
              <span class="tasks-section__detail-label">Due date</span>
              <p class="tasks-section__detail-value">
                {{ task.dueAt ? (task.dueAt | date: 'medium') : '—' }}
              </p>
            </div>
            @if (task.dueAt) {
              <div class="tasks-section__detail-field">
                <span class="tasks-section__detail-label">Reminder</span>
                <p class="tasks-section__detail-value">{{ reminderOffsetLabel(task) }}</p>
              </div>
              <div class="tasks-section__detail-field">
                <span class="tasks-section__detail-label">Reminder status</span>
                <p class="tasks-section__detail-value">{{ reminderStatusLabel(task.reminderStatus) }}</p>
              </div>
            }
          </div>
          <div class="tasks-section__detail-field tasks-section__detail-field--full">
            <span class="tasks-section__detail-label">Description</span>
            <p class="tasks-section__detail-value tasks-section__detail-value--description">
              {{ task.description || '—' }}
            </p>
          </div>
          @if (!isArchived()) {
            <div class="tasks-section__detail-actions">
              <app-admin-button variant="secondary" type="button" (click)="openEditFromDetail(task)">
                <app-admin-icon name="pencil" [size]="16" />
                Edit task
              </app-admin-button>
            </div>
          }
        </div>
      </app-admin-modal>
    }
  `,
  styles: `
    .tasks-section__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--cg-space-3);
      margin-bottom: var(--cg-space-4);
    }
    .tasks-section__head-actions {
      display: flex;
      align-items: center;
      gap: var(--cg-space-3);
      flex-wrap: wrap;
      justify-content: flex-end;
    }
    .tasks-section__filter {
      display: inline-flex;
      align-items: center;
      gap: var(--cg-space-2);
      margin: 0;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cg-color-text-secondary);
      cursor: pointer;
      user-select: none;
    }
    .tasks-section__filter-input {
      width: 1rem;
      height: 1rem;
      margin: 0;
      accent-color: var(--cg-color-primary, #072b61);
      cursor: pointer;
    }
    .tasks-section__title {
      display: flex;
      align-items: center;
      gap: var(--cg-space-2);
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--cg-color-text);
    }
    .tasks-section__count {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--cg-color-text-muted);
    }
    .tasks-section__title-cell {
      max-width: 14rem;
    }
    .tasks-section__title-link {
      display: inline-block;
      max-width: 100%;
      padding: 0;
      border: none;
      background: none;
      font: inherit;
      font-weight: 600;
      color: var(--cg-color-primary, #072b61);
      text-align: left;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.15s ease;
    }
    .tasks-section__title-link:hover {
      color: color-mix(in srgb, var(--cg-color-primary, #072b61) 75%, #000);
      text-decoration: underline;
    }
    .tasks-section__status {
      display: inline-flex;
      align-items: center;
      padding: 0.18rem 0.55rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.02em;
      border: 1px solid var(--cg-color-border);
      background: var(--cg-color-surface-muted, #f8fafc);
      color: var(--cg-color-text-secondary);
    }
    .tasks-section__status--new {
      border-color: #bfdbfe;
      background: #eff6ff;
      color: #1d4ed8;
    }
    .tasks-section__status--in-progress {
      border-color: #fde68a;
      background: #fffbeb;
      color: #b45309;
    }
    .tasks-section__status--completed {
      border-color: #bbf7d0;
      background: #ecfdf5;
      color: #047857;
    }
    .tasks-section__status-select-wrap {
      display: inline-flex;
      min-width: 8.5rem;
    }
    .tasks-section__status-select {
      min-width: 8.5rem;
      font-size: 0.8125rem;
    }
    .tasks-section__actions {
      display: flex;
      align-items: center;
      gap: var(--cg-space-2);
      flex-wrap: wrap;
    }
    .tasks-section__icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.75rem;
      height: 1.75rem;
      padding: 0;
      border: none;
      border-radius: var(--cg-radius-md, 0.375rem);
      background: transparent;
      color: var(--cg-color-text-muted, #64748b);
      cursor: pointer;
      transition:
        background 0.15s ease,
        color 0.15s ease;
    }
    .tasks-section__icon-btn:hover:not(:disabled) {
      background: var(--cg-color-surface-muted, #f1f5f9);
      color: var(--cg-color-text, #0f172a);
    }
    .tasks-section__icon-btn--danger:hover:not(:disabled) {
      background: #fef2f2;
      color: var(--cg-color-danger, #b91c1c);
    }
    .tasks-section__icon-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .tasks-section__form {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-4);
    }
    .tasks-section__form-actions {
      display: flex;
      flex-wrap: wrap;
      gap: var(--cg-space-3);
      justify-content: flex-end;
    }
    .tasks-section__notify-readonly {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-2);
      padding: var(--cg-space-3);
      border: 1px solid var(--cg-color-border);
      border-radius: var(--cg-radius-md, 0.375rem);
      background: var(--cg-color-surface-muted, #f8fafc);
    }
    .tasks-section__notify-label {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--cg-color-text-muted);
    }
    .tasks-section__notify-list {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-2);
    }
    .tasks-section__notify-list li {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--cg-space-2);
    }
    .tasks-section__notify-name {
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--cg-color-text);
    }
    .tasks-section__notify-role {
      font-size: 0.75rem;
      color: var(--cg-color-text-muted);
    }
    .tasks-section__error {
      margin: 0;
      font-size: 0.875rem;
      color: var(--cg-color-danger, #dc2626);
    }
    .tasks-section__detail {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-4);
    }
    .tasks-section__detail-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
      gap: var(--cg-space-4);
    }
    .tasks-section__detail-field {
      display: flex;
      flex-direction: column;
      gap: var(--cg-space-1);
    }
    .tasks-section__detail-field--full {
      padding-top: var(--cg-space-2);
      border-top: 1px solid var(--cg-color-border);
    }
    .tasks-section__detail-label {
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--cg-color-text-muted);
    }
    .tasks-section__detail-value {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 500;
      color: var(--cg-color-text);
      word-break: break-word;
    }
    .tasks-section__detail-value--description {
      font-weight: 400;
      line-height: 1.5;
      white-space: pre-wrap;
    }
    .tasks-section__detail-status-select {
      width: 100%;
      max-width: 12rem;
    }
    .tasks-section__detail-actions {
      display: flex;
      justify-content: flex-end;
      padding-top: var(--cg-space-2);
      border-top: 1px solid var(--cg-color-border);
    }
    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }
  `,
})
export class CrmTasksSectionComponent implements OnInit {
  readonly entityType = input.required<CrmTaskEntityType>();
  readonly entityId = input.required<string>();
  readonly area = input.required<CrmDetailArea>();
  readonly isArchived = input(false);
  readonly assignableUsers = input<CrmTaskAssigneeOption[]>([]);
  readonly currentUserId = input<string | null>(null);

  readonly changed = output<void>();

  private readonly tasksService = inject(CrmTasksService);
  private readonly taskReminderRefresh = inject(TaskReminderRefreshService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);

  readonly tasks = signal<CrmTaskDto[]>([]);
  readonly showCompletedTasks = signal(false);
  readonly visibleTasks = computed(() => {
    const rows = this.tasks();
    if (this.showCompletedTasks()) {
      return rows.filter((task) => task.status === 'COMPLETED');
    }
    return rows.filter((task) => task.status !== 'COMPLETED');
  });
  readonly emptyFilterTitle = computed(() =>
    this.showCompletedTasks() ? 'No completed tasks' : 'No active tasks',
  );
  readonly emptyFilterDescription = computed(() =>
    this.showCompletedTasks()
      ? 'Completed tasks will appear here once you mark them done.'
      : 'All tasks are completed. Check “Show completed tasks” to view them.',
  );
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly formModalOpen = signal(false);
  readonly detailModalOpen = signal(false);
  readonly selectedTask = signal<CrmTaskDto | null>(null);
  readonly formError = signal<string | null>(null);
  readonly editingTask = signal<CrmTaskDto | null>(null);
  readonly actionLoadingId = signal<string | null>(null);
  readonly minDueAtLocal = signal('');

  readonly statusOptions = TASK_STATUS_OPTIONS;
  readonly reminderOptions = TASK_REMINDER_OPTIONS;
  form: TaskFormState = emptyTaskForm();

  private pendingOpenTaskId: string | null = null;

  ngOnInit(): void {
    this.loadTasks();
  }

  reload(): void {
    this.loadTasks();
  }

  openTaskById(taskId: string): void {
    const task = this.tasks().find((row) => row.id === taskId);
    if (task) {
      this.openDetailModal(task);
      this.pendingOpenTaskId = null;
      return;
    }
    this.pendingOpenTaskId = taskId;
  }

  statusClass(status: CrmTaskStatus): string {
    switch (status) {
      case 'IN_PROGRESS':
        return 'tasks-section__status tasks-section__status--in-progress';
      case 'COMPLETED':
        return 'tasks-section__status tasks-section__status--completed';
      default:
        return 'tasks-section__status tasks-section__status--new';
    }
  }

  openCreateModal(): void {
    this.closeDetailModal();
    this.editingTask.set(null);
    this.form = emptyTaskForm();
    this.formError.set(null);
    this.refreshMinDueAtLocal();
    this.formModalOpen.set(true);
  }

  openDetailModal(task: CrmTaskDto): void {
    this.selectedTask.set(task);
    this.detailModalOpen.set(true);
  }

  closeDetailModal(): void {
    this.detailModalOpen.set(false);
    this.selectedTask.set(null);
  }

  openEditFromDetail(task: CrmTaskDto): void {
    this.closeDetailModal();
    this.openEditModal(task);
  }

  openEditModal(task: CrmTaskDto): void {
    this.editingTask.set(task);
    this.refreshMinDueAtLocal();
    const dueAtLocal = this.toDatetimeLocalValue(task.dueAt);
    this.form = {
      title: task.title,
      description: task.description ?? '',
      assignedTo: task.assignedTo,
      dueAtLocal: this.isDueAtLocalAllowed(dueAtLocal) ? dueAtLocal : '',
      reminderOffsetMinutes: task.reminderOffsetMinutes ?? CRM_TASK_DEFAULT_REMINDER_OFFSET_MINUTES,
    };
    this.formError.set(null);
    this.formModalOpen.set(true);
  }

  onDueAtChange(value: string): void {
    if (!value) {
      return;
    }

    if (!this.isDueAtLocalAllowed(value)) {
      this.form.dueAtLocal = '';
      this.formError.set('Due date must be today or later');
    }
  }

  closeFormModal(): void {
    if (this.saving()) return;
    this.formModalOpen.set(false);
    this.editingTask.set(null);
    this.formError.set(null);
    this.form = emptyTaskForm();
  }

  canSubmitForm(): boolean {
    return Boolean(this.form.title.trim() && this.form.assignedTo.trim());
  }

  submitForm(): void {
    const entityId = this.entityId();
    if (!entityId || !this.canSubmitForm()) return;

    const scheduleError = this.validateReminderSchedule();
    if (scheduleError) {
      this.formError.set(scheduleError);
      return;
    }

    const editing = this.editingTask();
    let payload: CreateCrmTaskPayload | UpdateCrmTaskPayload = {
      title: this.form.title.trim(),
      description: this.form.description.trim() || undefined,
      assignedTo: this.form.assignedTo.trim(),
    };

    if (this.form.dueAtLocal) {
      payload = {
        ...payload,
        dueAt: new Date(this.form.dueAtLocal).toISOString(),
        reminderOffsetMinutes: this.form.reminderOffsetMinutes,
      };
    } else if (editing?.dueAt) {
      payload = {
        ...payload,
        dueAt: null,
      };
    }

    this.saving.set(true);
    this.formError.set(null);

    const request$ = editing
      ? this.tasksService.update(
          this.area(),
          this.entityType(),
          entityId,
          editing.id,
          payload as UpdateCrmTaskPayload,
        )
      : this.tasksService.create(
          this.area(),
          this.entityType(),
          entityId,
          payload as CreateCrmTaskPayload,
        );

    request$.subscribe({
      next: (task) => {
        if (editing) {
          this.tasks.update((rows) => rows.map((row) => (row.id === task.id ? task : row)));
          if (this.selectedTask()?.id === task.id) {
            this.selectedTask.set(task);
          }
          this.toast.success('Task updated.');
        } else {
          this.tasks.update((rows) => [task, ...rows]);
          this.toast.success('Task created.');
        }
        this.saving.set(false);
        this.formModalOpen.set(false);
        this.editingTask.set(null);
        this.form = emptyTaskForm();
        this.changed.emit();
        this.taskReminderRefresh.notify();
      },
      error: (err) => {
        this.saving.set(false);
        const msg = err?.error?.message ?? 'Could not save task.';
        this.formError.set(msg);
        this.toast.error(msg);
      },
    });
  }

  onStatusChange(task: CrmTaskDto, status: CrmTaskStatus): void {
    if (status === task.status) return;

    const entityId = this.entityId();
    if (!entityId) return;

    this.actionLoadingId.set(task.id);
    this.tasksService
      .update(this.area(), this.entityType(), entityId, task.id, { status })
      .subscribe({
        next: (updated) => {
          this.tasks.update((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
          if (this.selectedTask()?.id === updated.id) {
            this.selectedTask.set(updated);
          }
          this.actionLoadingId.set(null);
          this.toast.success('Task status updated.');
          this.changed.emit();
          this.taskReminderRefresh.notify();
        },
        error: (err) => {
          this.actionLoadingId.set(null);
          this.loadTasks();
          const msg = err?.error?.message ?? 'Could not update task status.';
          this.toast.error(msg);
        },
      });
  }

  async confirmDelete(task: CrmTaskDto): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete task',
      message: `Delete "${task.title}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    const entityId = this.entityId();
    if (!entityId) return;

    this.actionLoadingId.set(task.id);
    this.tasksService.delete(this.area(), this.entityType(), entityId, task.id).subscribe({
      next: () => {
        this.tasks.update((rows) => rows.filter((row) => row.id !== task.id));
        if (this.selectedTask()?.id === task.id) {
          this.closeDetailModal();
        }
        this.actionLoadingId.set(null);
        this.toast.success('Task deleted.');
        this.changed.emit();
        this.taskReminderRefresh.notify();
      },
      error: (err) => {
        this.actionLoadingId.set(null);
        const msg = err?.error?.message ?? 'Could not delete task.';
        this.toast.error(msg);
      },
    });
  }

  private loadTasks(): void {
    const entityId = this.entityId();
    if (!entityId) return;

    this.loading.set(true);
    this.tasksService.list(this.area(), this.entityType(), entityId).subscribe({
      next: (rows) => {
        this.tasks.set(rows);
        this.loading.set(false);

        if (this.pendingOpenTaskId) {
          const task = rows.find((row) => row.id === this.pendingOpenTaskId);
          if (task) {
            this.openDetailModal(task);
            this.pendingOpenTaskId = null;
          }
        }
      },
      error: (err) => {
        this.loading.set(false);
        const msg = err?.error?.message ?? 'Could not load tasks.';
        this.toast.error(msg);
      },
    });
  }

  private validateReminderSchedule(): string | null {
    if (!this.form.dueAtLocal) {
      return null;
    }

    if (!TASK_REMINDER_OPTIONS.some((option) => option.value === this.form.reminderOffsetMinutes)) {
      return 'Reminder option is not supported';
    }

    const dueAt = new Date(this.form.dueAtLocal);
    if (Number.isNaN(dueAt.getTime())) {
      return 'Due date is invalid';
    }

    if (dueAt.getTime() <= Date.now()) {
      return 'Due date must be today or later';
    }

    const reminderAt = dueAt.getTime() - this.form.reminderOffsetMinutes * 60_000;
    if (reminderAt <= Date.now()) {
      return 'Reminder time has already passed';
    }

    return null;
  }

  private toDatetimeLocalValue(iso?: string): string {
    if (!iso) return '';

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';

    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private refreshMinDueAtLocal(): void {
    this.minDueAtLocal.set(this.buildMinDueAtLocal());
  }

  private buildMinDueAtLocal(): string {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }

  private isDueAtLocalAllowed(value: string): boolean {
    if (!value) return false;
    return value >= this.buildMinDueAtLocal();
  }

  private resolveUserLabel(userId: string | null | undefined): string {
    const normalized = userId?.trim();
    if (!normalized) return '—';
    return this.assignableUsers().find((user) => user.userId === normalized)?.label ?? normalized;
  }

  buildNotifiedUsers(): Array<{ userId: string; label: string; role: string }> {
    const users: Array<{ userId: string; label: string; role: string }> = [];
    const seen = new Set<string>();

    const addUser = (userId: string | null | undefined, label: string, role: string) => {
      const normalized = userId?.trim();
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      users.push({ userId: normalized, label, role });
    };

    const editing = this.editingTask();
    const creatorId = editing?.createdBy ?? this.currentUserId();
    const creatorLabel = editing
      ? editing.createdByDisplayName || editing.createdBy
      : this.resolveUserLabel(creatorId);
    addUser(creatorId, creatorLabel, 'Task creator');

    const assigneeId = this.form.assignedTo.trim();
    if (assigneeId) {
      addUser(assigneeId, this.resolveUserLabel(assigneeId), 'Task assignee');
    }

    return users;
  }

  reminderOffsetLabel(task: CrmTaskDto): string {
    const minutes = task.reminderOffsetMinutes ?? CRM_TASK_DEFAULT_REMINDER_OFFSET_MINUTES;
    const match = TASK_REMINDER_OPTIONS.find((option) => option.value === minutes);
    return match?.label ?? `${minutes} min`;
  }

  reminderStatusLabel(status?: CrmTaskReminderStatus): string {
    switch (status) {
      case 'sent':
        return 'Sent';
      case 'cancelled':
        return 'Cancelled';
      case 'failed':
        return 'Failed';
      case 'pending':
        return 'Pending';
      default:
        return '—';
    }
  }
}
